import React from 'react';
import { 
    Box,
    Typography,
    Link,
    Tooltip,
    Icon
} from '@material-ui/core';
import Card from '@material-ui/core/Card';
import { makeStyles } from '@material-ui/core/styles';


const useStyles = makeStyles((theme) => ({
    tooltip:{
        border: '1px solid #F2C80B',
    },
    creditDot:{
        width:32,
        height:32,
        background: 'linear-gradient(274.05deg, #CDACFC -8.09%, #8B3FFA 61.08%)',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    activityCard: {
        background: 'linear-gradient( #1C1F24 25.02%)',
        borderRadius: '8px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gridGap: '16px',
        border : `1px solid ${theme.palette.colors.violet[300]}`,
    },
    metricBox: {
        display: 'flex',
        alignItems: 'center',
        gridGap: '8px',
    },
    metricSquare: {
        width: '32px',
        height: '32px',
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#FFFFFF',
        fontWeight: 600,
        fontSize: '16px',
    },
    metricSquarePurple: {
        background: 'linear-gradient(274.05deg, #CDACFC -8.09%, #8B3FFA 61.08%)',
    },
    metricSquareGreen: {
        background: '#4CAF50',
    },
    metricSquareRed: {
        background: '#F74F58',
    },
    infoLink: {
        color: '#FFA726',
        textDecoration: 'underline',
        cursor: 'pointer',
        '&:hover': {
            color: '#FF9800',
        },
    },
}))

const ProgressAccordionDetail = ({
    credits = 0, 
    level_id,
    subjectsInProgress = 0,
    daysRemaining = 0,
    is_new_modality_credits = false,
    blockStartDate = null,
    blockEndDate = null,
    completedInBlock = 0
}) => {
    const classes = useStyles();

    const formatDate = (dateString) => {
        if (!dateString) return '00/00/00';
        const parts = dateString.split('/');
        if (parts.length !== 3) return dateString;
        const [day, month, year] = parts;
        return `${day}/${month}/${year.slice(-2)}`;
    };

    return (
        <Box display='flex' gridGap={24} flexDirection='column' pb={3}>
            {/* Card: Actividad en curso */}
            {is_new_modality_credits && (<Card className={classes.activityCard} elevation={0}>
                <Box>
                    <Typography variant="h4" style={{ color: '#FFFFFF', fontWeight: 700 }}>
                        Actividad en curso
                    </Typography>
                    <Typography variant="body2" style={{ color: '#BDBDBD', marginTop: '4px' }}>
                        Período: <span style={{ color: '#CDACFC' }}>{formatDate(blockStartDate)}</span> a <span style={{ color: '#CDACFC' }}>{formatDate(blockEndDate)}</span>
                    </Typography>
                </Box>
                
                <Box display='flex' flexDirection='column' gridGap={12}>
                    {/* Materias en curso */}
                    <Box className={classes.metricBox}>
                        <Box className={`${classes.metricSquare} ${classes.metricSquarePurple}`}>
                            {subjectsInProgress}
                        </Box>
                        <Typography variant="body1" style={{ color: '#FFFFFF' }}>
                            Materias en curso
                        </Typography>
                    </Box>
                    
                    {/* Créditos disponibles */}
                    <Box className={classes.metricBox}>
                        <Box className={`${classes.metricSquare} ${classes.metricSquarePurple}`}>
                            {credits}
                        </Box>
                        <Typography variant="body1" style={{ color: '#FFFFFF' }}>
                            Créditos disponibles
                        </Typography>
                    </Box>

                    {/* Materias terminadas */}
                    <Box className={classes.metricBox}>
                        <Box className={`${classes.metricSquare} ${classes.metricSquarePurple}`}>
                            {completedInBlock}
                        </Box>
                        <Typography variant="body1" style={{ color: '#FFFFFF' }}>
                            Materias terminadas
                        </Typography>
                    </Box>
                    
                    {/* Días para finalizar */}
                    <Box className={classes.metricBox}>
                        <Box className={`${classes.metricSquare} ${subjectsInProgress === 0 && credits === 0 ? classes.metricSquareRed : classes.metricSquareGreen}`}>
                            {daysRemaining}
                        </Box>
                        <Typography variant="body1" style={{ color: '#FFFFFF' }}>
                         {subjectsInProgress === 0 && credits === 0 ? 'Días para recibir créditos' : 'Días para finalizar tus materias '}
                        </Typography>
                    </Box>
                </Box>
                
                <Box mt={1}>
                    <Typography variant="body2" className='text-dark-black-100'>
                        Puedes completar hasta {level_id === 'BA' || level_id === 'LI' ? '2 materias por mes según tu progreso.' : '1 materia por cada 5 semanas.'}{' '}
                        <Link 
                            className={classes.infoLink}
                            href="https://soportetalisis.zendesk.com/hc/es-419/sections/37132624900119-Reglas-para-consumo-de-materias"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Conoce las reglas para avanzar aquí
                        </Link>
                    </Typography>
                </Box>
            </Card>)}

            {/* Resumen del Programa */}
            <Box display='flex' flexDirection='column' gridGap={8}>
                <Typography variant="h4">Resumen del Programa</Typography>
                <Typography variant='body2' className='text-dark-black-100'>
                    {level_id !== 'BA' 
                        ? 'El orden en el que aparecen las materias es recomendado, pero puedes tomarlas en el orden que mejor se te acomode.' 
                        : 'Debes completar cada cuatrimestre antes de pasar al siguiente, el orden de las materias es recomendado, pero puedes tomarlas en el orden que mejor se te acomode.'
                    }
                </Typography>
            </Box>

        {!is_new_modality_credits && (
            <Box display='flex' gridGap={12} flexDirection='column'>
                <Box display='flex' alignItems='center' gridGap={8}>
                    <Tooltip arrow classes={{ tooltip: classes.tooltip }}
                        title="Puedes cursar hasta 2 materias al estar inscrito en este programa siempre que sean del mismo cuatrimestre." arrow classes={{ tooltip: classes.tooltip }}>
                        <Icon className="ri-error-warning-line text-warning-300 font-size-20"/>
                    </Tooltip>
                    <Typography variant="body1">Créditos disponibles:</Typography>
                    <Box className={classes.creditDot}>
                        {credits}
                    </Box>
                </Box>
            </Box>
        )}
        </Box>
    );
};

export default ProgressAccordionDetail;
